import React, { useState } from 'react';
import { Pencil, ArrowRightLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { toast } from 'react-toastify';

const AttendanceTabs = ({ goingList = [], waitingList = [], notgoingList = [], currentUserId, editable = false, event = {}, onAttendeeChange }) => {
    const [activeTab, setActiveTab] = useState('going');
    const [isProcessing, setIsProcessing] = useState(false);
    const navigate = useNavigate();


    const handleMove = async (attendee, fromList, toList) => {
        // Prevent the button from being clicked during processing
        if (isProcessing) return;

        if (!window.confirm(`Are you sure you want to move ${attendee.name} from ${fromList} to ${toList} list?`)) {
            return;
        }

        setIsProcessing(true);
        try {
            const response = await axios.post('/otgservices/moveAttendee.php', {
                event_id: attendee.event_id,
                user_id: attendee.user_id,
                from_list: fromList,
                to_list: toList
            });

            if (response.data.success) {
                toast.success(`Attendee moved to ${toList} list`);
                // Call the parent component's callback to update the lists
                if (onAttendeeChange) {
                    onAttendeeChange(attendee.user_id, fromList, 'move', toList);
                }
            } else {
                toast.error(response.data.error || 'Failed to move attendee');
            }
        } catch (error) {
            console.error('Error moving attendee:', error);
            toast.error('Error connecting to server');
        } finally {
            setIsProcessing(false);
        }
    };

    const renderTable = (attendees, listType) => {
        if (!attendees || attendees.length === 0) {
            return <p className="text-center py-4 text-gray-500">No attendees in this list.</p>;
        }
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="py-2 px-4 text-left border-b"></th>
                        <th className="py-2 px-4 text-left border-b">Name</th>
                        <th className="py-2 px-4 text-left border-b">Companions</th>
                        <th className="py-2 px-4 text-left border-b">Rig</th>
                        {editable && <th className="py-2 px-4 text-left border-b">Phone Number</th>}
                        {editable && <th className="py-2 px-1 text-center border-b">Actions</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {attendees.map((attendee, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="py-3 px-4 border-b w-20">
                                {attendee.avatar ? (
                                    <img
                                        src={attendee.avatar}
                                        alt={`${attendee.name}'s avatar`}
                                        className="w-16 h-12 rounded-lg object-cover"
                                    />
                                ) : (
                                    <div className="w-16 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 text-xl">
                                        {attendee.name.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </td>
                            <td className="py-3 px-4 border-b">{attendee.name}</td>
                            <td className="py-3 px-4 border-b">{attendee.companions || attendee.travel_companions || 'None'}</td>
                            <td className="py-3 px-4 border-b">{attendee.rig || attendee.rig_make_model || 'N/A'}</td>
                            {editable && <td className="py-3 px-4 border-b">{attendee.phone || attendee.phone_number}</td>}

                            <td className="py-3 px-1 border-b text-center">
                                <div className="flex items-center justify-center space-x-2">
                                    {/* Edit button */}
                                    { (currentUserId === attendee.user_id) && (
                                        <button
                                            onClick={ () => {
                                                navigate(`/registration/${attendee.event_id}/${attendee.user_id}`);
                                            }}
                                            className="text-blue-500 hover:text-blue-700"
                                            title="Edit registration"
                                            disabled={isProcessing}
                                        >
                                            <Pencil size={18} />
                                        </button>
                                    )
                                    }

                                    {/* Move buttons for Going list */}
                                    {(editable && listType === 'going') && (
                                        <>
                                            <button
                                                onClick={() => handleMove(attendee, 'going', 'waiting')}
                                                className="text-orange-500 hover:text-orange-700"
                                                title="Move to waiting list"
                                                disabled={isProcessing}
                                            >
                                                <ArrowRightLeft size={18}/>
                                            </button>
                                            <button
                                                onClick={() => handleMove(attendee, 'going', 'notgoing')}
                                                className="text-red-500 hover:text-red-700"
                                                title="Move to not going list"
                                                disabled={isProcessing}
                                            >
                                                <ArrowRightLeft size={18}/>
                                            </button>
                                        </>
                                    )}

                                    {/* Move buttons for Waiting list */}
                                    {(editable && listType === 'waiting') && (
                                        <>
                                            <button
                                                onClick={() => handleMove(attendee, 'waiting', 'going')}
                                                className="text-green-500 hover:text-green-700"
                                                title="Move to going list"
                                                disabled={isProcessing}
                                            >
                                                <ArrowRightLeft size={18}/>
                                            </button>
                                            <button
                                                onClick={() => handleMove(attendee, 'waiting', 'notgoing')}
                                                className="text-red-500 hover:text-red-700"
                                                title="Move to not going list"
                                                disabled={isProcessing}
                                            >
                                                <ArrowRightLeft size={18}/>
                                            </button>
                                        </>
                                    )}

                                    {/* Move buttons for Not Going list */}
                                    {(editable && listType === 'notgoing') && (
                                        <>
                                            <button
                                                onClick={() => handleMove(attendee, 'notgoing', 'going')}
                                                className="text-green-500 hover:text-green-700"
                                                title="Move to going list"
                                                disabled={isProcessing}
                                            >
                                                <ArrowRightLeft size={18}/>
                                            </button>
                                            <button
                                                onClick={() => handleMove(attendee, 'notgoing', 'waiting')}
                                                className="text-orange-500 hover:text-orange-700"
                                                title="Move to waiting list"
                                                disabled={isProcessing}
                                            >
                                                <ArrowRightLeft size={18}/>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </td>

                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const getTabContent = () => {
        switch (activeTab) {
            case 'going':
                return renderTable(goingList, 'going');
            case 'waiting':
                return renderTable(waitingList, 'waiting');
            case 'notgoing':
                return renderTable(notgoingList, 'notgoing');
            default:
                return renderTable(goingList, 'going');
        }
    };

    return (
        <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow">
            {/* Tab Navigation */}
            <div className="flex border-b">
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium flex-1 ${
                        activeTab === 'going'
                            ? 'border-b-2 border-blue-500 text-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('going')}
                >
                    Going ({goingList.length})
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium flex-1 ${
                        activeTab === 'waiting'
                            ? 'border-b-2 border-blue-500 text-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('waiting')}
                >
                    Waiting ({waitingList.length})
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium flex-1 ${
                        activeTab === 'notgoing'
                            ? 'border-b-2 border-red-500 text-red-600'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('notgoing')}
                >
                    Not Going ({notgoingList.length})
                </button>
            </div>
            {/* Tab Content */}
            <div className="p-4">
                {getTabContent()}
            </div>
        </div>
    );
};

export default AttendanceTabs;